import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import tenantService from '../services/tenantService';
import { Organization, TenantContextType } from '../types';

const TenantContext = createContext<TenantContextType | null>(null);

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Load organizations on mount (only if user has a token)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadOrganizations();
    }
  }, []);

  const loadOrganizations = async (): Promise<void> => {
    try {
      setLoading(true);
      const data = await tenantService.getMyOrganizations();
      setOrganizations(data.organizations || []);

      // Set current organization if available
      const currentOrgId = tenantService.getCurrentOrganizationId();
      if (currentOrgId) {
        const currentOrg = data.organizations?.find(
          (org) => org.id.toString() === currentOrgId
        );
        if (currentOrg) {
          setOrganization(currentOrg);
        }
      } else if (data.organizations?.length > 0) {
        // Auto-select first organization
        switchOrganization(data.organizations[0].id);
      }
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const switchOrganization = async (organizationId: number): Promise<void> => {
    try {
      await tenantService.switchOrganization(organizationId);
      const org = organizations.find(
        (o) => o.id === organizationId
      );
      setOrganization(org || null);
      tenantService.setCurrentOrganizationId(organizationId);
      // Reload page to refresh data with new org context
      window.location.reload();
    } catch (error) {
      console.error('Error switching organization:', error);
      throw error;
    }
  };

  const createOrganization = async (orgData: any): Promise<any> => {
    try {
      const data = await tenantService.registerOrganization(orgData);
      await loadOrganizations();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const updateOrganization = async (orgData: any): Promise<any> => {
    try {
      const data = await tenantService.updateOrganization(orgData);
      setOrganization(data.organization);
      return data;
    } catch (error) {
      throw error;
    }
  };

  const value: TenantContextType = {
    organization,
    organizations,
    loading,
    switchOrganization,
    createOrganization,
    updateOrganization,
    loadOrganizations,
    hasOrganization: !!organization,
  };

  return (
    <TenantContext.Provider value={value}>{children}</TenantContext.Provider>
  );
};

export default TenantContext;
